import type { Comment, CommentTargetType } from '@/@types/comment'
import { ADD_COMMENT } from '@/constants/api'
import { css } from '@emotion/react'
import { Button } from '@ruguoapp/jike-fe-components'
import { RepostIcon } from '@ruguoapp/jike-fe-icons'
import { compact, first, flatMap, isEmpty } from 'lodash-es'
import { type HTMLAttributes, useCallback, useRef } from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import { Attachment } from '@/states/attachment'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { post } from '@/utils/request'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { MIME_TYPES } from '@mantine/dropzone'
import { motion } from 'motion/react'
import AttachmentDropzone, { AttachmentDropzoneProps } from '../Attachment/AttachmentDropzone'
import AttachmentsPreview from '../Attachment/AttachmentsPreview'
import AttachmentsUploadService from '../Attachment/AttachmentsUploadService'
import MixedAttachmentPicker from '../Attachment/pickers/MixedAttachmentPicker'
import { filterFiles, filterPicAttachments } from '../Attachment/utils'
import Avatar from '../Avatar'
import type { LexicalEditorHandle } from '../Editor/LexicalEditor'
import CommentContentEditor, { CommentContentEditorProps } from './CommentContentEditor'
import { CommentFormSchema, type CommentFormValue, type ReplyToComment } from './types'

export interface CommentFormProps
	extends Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'>,
		Pick<CommentContentEditorProps, 'autoFocus'> {
	onSubmit?: (data: Comment) => void
	replyToComment?: ReplyToComment | null
	targetId: string
	targetType: CommentTargetType
}

export default function CommentForm({
	onSubmit,
	replyToComment,
	targetId,
	targetType,
	autoFocus,
	...props
}: CommentFormProps) {
	const contentEditorRef = useRef<LexicalEditorHandle>(null)
	const toComment = replyToComment?.to

	const user = useCurrentUser()

	const methods = useForm<CommentFormValue>({
		defaultValues: {
			content: '',
			attachments: [],
			isSyncToPersonalUpdate: false,
		},
		resolver: zodResolver(CommentFormSchema),
		mode: 'onChange',
	})
	const {
		formState: { errors },
	} = methods
	const {
		fields: attachments,
		append: addAttachment,
		remove: removeAttachments,
		update: updateAttachment,
	} = useFieldArray<CommentFormValue, 'attachments', 'id'>({
		name: 'attachments',
		control: methods.control,
	})

	const handleAddAttachment = (newAttachments: Attachment[]) => {
		const { success, error } = CommentFormSchema.shape.attachments.safeParse([
			...attachments,
			...newAttachments,
		])
		if (!success) {
			const firstError = first(error.errors)?.message
			firstError && toast.error(firstError)
			return
		}
		addAttachment(newAttachments)
	}

	const handleSubmit = useCallback(
		async (data: CommentFormValue) => {
			const pictureAttachments = filterPicAttachments(data.attachments)
			if (isEmpty(data.content) && isEmpty(pictureAttachments)) {
				toast('请输入内容')
				return
			}
			const pictureKeys = compact(pictureAttachments.map((item) => item.cdn?.file.key))

			const payload = {
				targetType,
				targetId,
				content: data.content,
				syncToPersonalUpdates: data.isSyncToPersonalUpdate,
				pictureKeys,
				force: false,
				threadId: replyToComment?.threadId ?? undefined,
				replyToCommentId: replyToComment?.to?.id,
			}

			return post<{ data: Comment }>(ADD_COMMENT, payload).then((res) => {
				methods.reset()
				contentEditorRef.current?.clear()
				onSubmit?.(res.data.data)
			})
		},
		[replyToComment?.threadId, replyToComment?.to?.id, methods, onSubmit, targetId, targetType]
	)

	const handleAttachmentsDropped: AttachmentDropzoneProps['onDrop'] = (files) => {
		const attachmentGroup = filterFiles(files)
		handleAddAttachment(flatMap(attachmentGroup))
	}

	return (
		<FormProvider {...methods}>
			<AttachmentDropzone
				accept={[MIME_TYPES.png, MIME_TYPES.jpeg]}
				onDrop={handleAttachmentsDropped}
			>
				<div
					css={css`
						display: grid;
						grid-template-columns: auto 1fr;
						gap: 10px;
						background: var(--bg-body-1, #fff);
					`}
					{...props}
				>
					<Avatar
						data={user}
						css={css`
							margin: 4px 0;
						`}
					/>
					<form
						onSubmit={methods.handleSubmit(handleSubmit)}
						css={css`
							padding-top: 10px;
							overflow: hidden;
						`}
					>
						<Controller
							name="content"
							render={({ field }) => (
								<CommentContentEditor
									autoFocus={autoFocus}
									to={toComment}
									{...field}
									ref={contentEditorRef}
								/>
							)}
						/>

						<footer
							css={css`
								margin-top: 12px;
								display: flex;
								justify-content: space-between;
								align-items: center;
							`}
						>
							<MixedAttachmentPicker
								noVideo
								noLink
								isPicturePickerDisabled={attachments.length > 0}
								onAdd={handleAddAttachment}
								css={css`
									margin-top: 8px;
									background-color: var(--bg-on-body-1, #fff);
								`}
							/>

							<div
								css={css`
									display: flex;
									align-items: center;
									gap: 10px;
								`}
							>
								<Controller
									name="isSyncToPersonalUpdate"
									render={({ field }) => (
										<Button
											onClick={() => field.onChange(!field.value)}
											appearance={field.value ? 'primary' : 'ghost'}
											css={css`
												&.jk-btn {
													min-width: unset;
													border-radius: 50%;
													padding: 4px;
													aspect-ratio: 1;
												}
											`}
											size="sm"
										>
											<RepostIcon
												css={css`
													width: 16px;
													height: 16px;
												`}
												color="currentColor"
											/>
										</Button>
									)}
								/>

								<Button appearance="primary" size="sm" type="submit">
									回复{methods.watch('isSyncToPersonalUpdate') && '并同步到动态'}
								</Button>
							</div>
						</footer>

						<AttachmentsPreview
							data={attachments}
							onDelete={(id) => {
								const index = attachments.findIndex((item) => item.id === id)
								index !== -1 && removeAttachments(index)
							}}
						/>
						<ErrorMessage
							name="attachments"
							errors={errors}
							render={({ message }) => (
								<motion.p
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									css={css`
										font-size: 14px;
										font-weight: 500;
										color: var(--tint-error);
										margin-top: 12px;
										margin-bottom: unset;
									`}
								>
									{message}
								</motion.p>
							)}
						/>

						{!errors.attachments && (
							<AttachmentsUploadService data={attachments} onUpdate={updateAttachment} />
						)}
					</form>
				</div>
			</AttachmentDropzone>
		</FormProvider>
	)
}
